import {
  Dialog as HeadlessDialog,
  DialogBackdrop as HeadlessDialogBackdrop,
  DialogPanel as HeadlessDialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { classNames } from '@src/utils/classNames';
import CloseButton from '@components/CloseButton.tsx';
import { useIsSmallScreen } from '@src/hooks/useMediaQuery';
import { useMemo } from 'react';
import { Heading4 } from '../typography/Heading4';
import { Heading5 } from '../typography/Heading5';

export type BaseModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  dismissible?: boolean;
  title?: string;
  titleComponent?: React.JSX.Element;
  content?: React.JSX.Element;
  showCloseIcon?: boolean;
  actions?: React.JSX.Element;
  testId?: string;
  passthrough?: {
    panel?: { className?: string };
  };
  onClose?: () => void;
};

export default function BaseModal({
  open,
  setOpen,
  dismissible = true,
  title,
  titleComponent,
  content,
  actions,
  showCloseIcon = dismissible,
  testId,
  passthrough,
  onClose,
}: BaseModalProps) {
  const isPhone = useIsSmallScreen();

  const defaultTitleComponent = useMemo(
    () =>
      isPhone ? (
        <Heading4
          className="ml-6 w-[calc(100%-48px)] text-center"
          data-testid="modal-title"
        >
          {title}
        </Heading4>
      ) : (
        <Heading5 data-testid="modal-title">{title}</Heading5>
      ),
    [isPhone, title],
  );

  return (
    <Transition show={open} data-testid={testId}>
      <HeadlessDialog
        as="div"
        className="relative z-[var(--z-index-modal)]"
        onClose={() => {
          if (!dismissible) {
            return;
          }
          onClose?.();
          setOpen(false);
        }}
      >
        <TransitionChild
          enter="animate-ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <HeadlessDialogBackdrop className="fixed inset-0 bg-black/70" />
        </TransitionChild>

        <div className="fixed inset-0  z-[var(--z-index-overlay)] overflow-y-auto">
          <div className="flex min-h-full items-end justify-center text-center sm:items-center">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <HeadlessDialogPanel
                className={classNames(
                  'relative transform rounded-t-lg sm:rounded-lg bg-white border-white border p-4 sm:p-6 text-left transition-all flex flex-col max-h-[100vh] overflow-auto',
                  passthrough?.panel?.className,
                )}
              >
                {(!!title || !!titleComponent || !!showCloseIcon) && (
                  <span className="flex justify-between mb-4">
                    {title && defaultTitleComponent}
                    {titleComponent ?? <span></span>}
                    {showCloseIcon && (
                      <CloseButton
                        testId="close-modal-button"
                        onClick={() => {
                          onClose?.();
                          setOpen(false);
                        }}
                        tabIndex={0}
                        className="!text-slate-900 cursor-pointer"
                      />
                    )}
                  </span>
                )}

                {content}
                {actions}
              </HeadlessDialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  );
}
